// Special billing deals ("special agreements"): per-client charge/rebate rows against a
// category-"Special" catalog product (backend table `special_assignment`; design:
// docs/plans/special-products.md). Two read surfaces share these shapes:
//   • the staff CRUD at /admin/billing/specials (docs/api-types/admin-special-assignments.md) —
//     the full Deal Object incl. the internal `note` and `billed_event_count`;
//   • the tenant read at GET /clients/specials (docs/api-types/clients.md § Special Agreements) —
//     ACTIVE deals only, allowlist-transformed (the staff note is never exposed).
// Money/percent values are always-positive DECIMAL(10,4) STRINGS — the sign is derived from `kind`
// at emit time. Send strings, never floats.

export type SpecialDealKind = 'charge' | 'discount_fixed' | 'discount_percent';

/** Attachment type — v1: virtual machines only (`parent_id` = the NetBox VM id). */
export const SPECIAL_DEAL_PARENT_VM = 'virtual_machine';

/**
 * The catalog category whose products are billable through deals. The backend resolves it by this
 * UNIQUE name (`SpecialAssignment::CATEGORY_NAME`) — ids drift between environments, the name does not.
 */
export const SPECIAL_CATEGORY_NAME = 'Special';

/** Embedded client reference on the staff Deal Object (`name` null on a dangling row). */
export interface SpecialDealClientRef {
  id: number;
  name: string | null;
}

/** Embedded category-Special product reference on the staff Deal Object. */
export interface SpecialDealProductRef {
  id: number;
  product_key: string;
  label: string;
  /** Billing cadence: "once" | "monthly" | "yearly" (kept a free string, like AdminProduct). */
  recurring: string;
}

/** The staff Deal Object (`/admin/billing/specials`, data keys `specials` / `special`). */
export interface AdminSpecialDeal {
  id: number;
  client: SpecialDealClientRef;
  product: SpecialDealProductRef;
  kind: SpecialDealKind;
  /** Always-positive DECIMAL(10,4) string: EUR excl. VAT (charge/discount_fixed) or percent (0,100]. */
  value: string;
  /** `"virtual_machine"` for attached deals; null = standalone. Both-or-neither with `parent_id`. */
  parent_type: string | null;
  /** The NetBox VM id (must be billing-enrolled under the same client). */
  parent_id: number | null;
  /** `Y-m-d` anchor — set iff standalone with a monthly/yearly product (day ≤ 28 for monthly). */
  next_billing_at: string | null;
  /** Budget: null = until deactivated; 1 = one-shot; 0 = spent (kept for audit). */
  occurrences_left: number | null;
  /** Staff kill switch; auto-cleared when the budget hits 0. Inactive deals never emit. */
  active: boolean;
  /** Staff author (audit; degrades on user deletion). */
  user_id: number | null;
  /** Internal staff memo — never rendered on an invoice, never exposed on the tenant read. */
  note: string | null;
  /**
   * `billing_event` rows referencing this deal. Drives the mutability/delete affordances: identity
   * fields (client, product, kind, attachment) freeze once > 0, and DELETE turns into a deactivate.
   */
  billed_event_count: number;
  created_at: string | null;
  updated_at: string | null;
}

/** One row of GET /clients/specials — the tenant read (ACTIVE deals only, no staff internals). */
export interface ClientSpecialAgreement {
  id: number;
  /** Labels of the underlying category-Special product; nulls on a dangling row. */
  product: { label: string | null; recurring: string | null };
  kind: SpecialDealKind;
  value: string;
  parent_type: string | null;
  parent_id: number | null;
  /** The attached VM's display name (from its billing enrollment); null for standalone deals. */
  parent_name: string | null;
  next_billing_at: string | null;
  occurrences_left: number | null;
}

// POST /admin/billing/specials. Unlike the tenant routes, this staff back-office API takes the
// billed organization's `clientID` in the BODY (not from the impersonation header) — when
// impersonating org X, send X's id explicitly.
export interface SpecialDealCreateRequest {
  clientID: number;
  productID: number;
  kind: SpecialDealKind;
  value: string;
  parent_type?: string;
  parent_id?: number;
  /** Required iff standalone + monthly/yearly product; ≥ today; day ≤ 28 for monthly. */
  next_billing_at?: string;
  /** ≥ 1 (0 is refused at creation); omit for unlimited. `once` products default to and require 1. */
  occurrences_left?: number;
  note?: string;
}

// PATCH /admin/billing/specials/{id} — partial, per the mutability matrix: identity fields
// (clientID/productID/kind/parent_*) freeze once billed_event_count > 0; value/note/
// occurrences_left/active stay free. `occurrences_left: null` = unlimited; PATCHing it to 0 also
// deactivates (the explicit {occurrences_left: 0, active: true} combo is refused).
export interface SpecialDealUpdateRequest {
  clientID?: number;
  productID?: number;
  kind?: SpecialDealKind;
  value?: string;
  parent_type?: string | null;
  parent_id?: number | null;
  next_billing_at?: string;
  occurrences_left?: number | null;
  note?: string | null;
  active?: boolean;
}
